import React, { useState, useMemo, useEffect } from "react";
import {handleRecoAddToCart, handleRecoClick} from "../../common/services";
import {formatProductPrice, toNumberPrice} from "../utils/priceUtils";

const ShopifyAddToCart = ({ product, index, query, clientKey, currencyCode = null,roundingEnabled=null }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [showQuickAdd, setShowQuickAdd] = useState(false);
    const [qty, setQty] = useState(1);
    const [showMaxMsg, setShowMaxMsg] = useState(false);

    const variants = product.variantMap || [];

    const handleClick = () => {
        handleRecoClick(product, {index, query, clientKey, recoSrc: "voice"});
    };

    // Build option groups dynamically
    const optionGroups = useMemo(() => {
        const acc = {};
        variants.forEach((v) => {
            v.options.forEach((opt) => {
                if (!acc[opt.name]) acc[opt.name] = new Set();
                acc[opt.name].add(opt.value);
            });
        });
        Object.keys(acc).forEach((key) => {
            acc[key] = Array.from(acc[key]);
        });
        return acc;
    }, [variants]);

    const [selectedOptions, setSelectedOptions] = useState(() => {
        const obj = {};
        if (variants[0]) {
            variants[0].options.forEach((opt) => {
                obj[opt.name] = opt.value;
            });
        }
        return obj;
    });

    const [selectedVariant, setSelectedVariant] = useState(variants[0] || null);

    useEffect(() => {
        const matched = variants.find((v) =>
            v.options.every((opt) => selectedOptions[opt.name] === opt.value)
        );
        if (matched) setSelectedVariant(matched);
    }, [selectedOptions, variants]);

    const maxQty = selectedVariant?.inventoryQuantity || 1;

    useEffect(() => {
        if (!selectedVariant) return;
        setQty((prev) => Math.min(prev, maxQty));
    }, [selectedVariant]);

    const isOptionAvailable = (name, value) => {
        return variants.some((v) => {
            if (!v.stock_status) return false;
            return v.options.every((opt) => {
                if (opt.name === name) return opt.value === value;
                return selectedOptions[opt.name] === opt.value;
            });
        });
    };

    const handleOptionSelect = (name, value) => {
        setSelectedOptions((prev) => ({ ...prev, [name]: value }));
    };

    const addToCart = () => {
        if (!selectedVariant) return;

        setIsLoading(true);

        window.parent.postMessage(
            {
                type: "ADD_TO_CART",
                variantId: selectedVariant.id,
                quantity: qty,
            },
            "*"
        );

        setTimeout(() => setIsLoading(false), 500);

        handleRecoAddToCart(product, {
            index,
            query,
            clientKey,
            recoSrc: "voice",
        });
    };

    const quickAdd = (e) => {
        e.stopPropagation();
        setShowQuickAdd(true);
    };

    const selectedPrice = toNumberPrice(selectedVariant?.price);
    const selectedCompareAtPrice = toNumberPrice(selectedVariant?.compare_at_price);
    const hasDiscount = selectedCompareAtPrice !== null
        && selectedPrice !== null
        && selectedCompareAtPrice > selectedPrice;

    return (
        <>
            <button
                className="w-full text-[clamp(10px,0.84vw,12px)] bg-black text-white py-2 rounded transition hover:opacity-90 disabled:opacity-50"
                disabled={isLoading || !product.stock_status}
                onClick={(e) => {
                    e.stopPropagation();
                    if (variants.length > 1) quickAdd(e);
                    else addToCart();
                }}
            >
                {isLoading
                    ? "Adding..."
                    : !product.stock_status
                        ? "Out of stock"
                        : variants.length > 1
                            ? "Quick Add"
                            : "Add to cart"}
            </button>

            {showQuickAdd && (
                <div
                    className="fixed inset-0 z-[600] flex items-center justify-center bg-black/50"
                    onClick={() => setShowQuickAdd(false)}
                >
                    <div
                        className="flex min-h-[50vh] lg:min-h-[60vh] bg-white rounded-xl p-5 w-[90%] max-w-3xl relative max-h-[100vh] overflow-auto"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            className="absolute top-3 right-3 text-lg"
                            onClick={() => setShowQuickAdd(false)}
                        >
                            ✕
                        </button>

                        <div className="flex gap-8 flex-col md:flex-row items-center">
                            <img
                                src={product.image}
                                alt={product.name}
                                className="object-contain w-1/2"
                            />

                            <div className="w-full md:w-1/2 flex flex-col justify-center">
                                <a
                                    className="text-gray-800 text-[clamp(14px,0.9375vw,18px)] hover:underline cursor-pointer"
                                    onClick={handleClick}
                                >
                                    {product.name}
                                </a>

                                <p className="mt-1 text-gray-600 ">
                                    {selectedVariant ? (
                                        <span className="flex gap-2 items-center">
                      {hasDiscount ? (
                          <>
                          <span className="text-gray-600 text-[clamp(12px,0.73vw,14px)]">
                            {formatProductPrice(selectedVariant.price, currencyCode,roundingEnabled)}
                          </span>
                              <span className="line-through text-gray-400">
                            {formatProductPrice(selectedVariant.compare_at_price, currencyCode,roundingEnabled)}
                          </span>
                          </>
                      ) : (
                          <span className="text-gray-600 text-[clamp(12px,0.73vw,14px)]">
                          {formatProductPrice(selectedVariant.price, currencyCode,roundingEnabled)}
                        </span>
                      )}
                    </span>
                                    ) : (
                                        <span className="text-black font-semibold">{formatProductPrice(product.price, currencyCode,roundingEnabled)}</span>
                                    )}
                                </p>

                                {/* SWATCHES */}
                                <div className="mt-4 space-y-4">
                                    {Object.entries(optionGroups).map(([name, values]) => (
                                        <div key={name}>
                                            <p className=" mb-2 text-[clamp(12px,0.84vw,16px)]">{name}</p>
                                            <div className="flex gap-2 flex-wrap">
                                                {values.map((value) => {
                                                    const selected = selectedOptions[name] === value;
                                                    const available = isOptionAvailable(name, value);
                                                    return (
                                                        <button
                                                            key={value}
                                                            disabled={!available}
                                                            onClick={() => handleOptionSelect(name, value)}
                                                            className={`px-3 py-1 border rounded text-[clamp(12px,0.73vw,14px)] ${
                                                                selected ? "bg-black text-white" : ""
                                                            } ${!available ? "opacity-30 cursor-not-allowed" : ""}`}
                                                        >
                                                            {value}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* QTY SELECTOR */}
                                <div className="flex items-center gap-2 mt-4">
                                    <div className="border border-[#e5e7eb] py-2 min-w-[142px] flex justify-around">
                                        <button
                                        className="border-0 px-3 py-1"
                                        onClick={() => setQty((prev) => Math.max(1, prev - 1))}
                                    >
                                       
                                        <img 
                                        src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAYCAYAAADgdz34AAAACXBIWXMAAAsTAAALEwEAmpwYAAAAIUlEQVR4nGNgGAWjYBSMApLBfyrhgbNgFIyCUTAKGDABAOkcI91xODUvAAAAAElFTkSuQmCC"
                                            alt="minus" 
                                            className="w-[14px]"
                                        />
                                    </button>
                                    <span>{qty}</span>
                                    <button
                                        className="border-0 px-3 py-1 disabled:opacity-30"
                                        onClick={() => {
                                            if (qty >= maxQty) {
                                                setShowMaxMsg(true);
                                                setTimeout(() => setShowMaxMsg(false), 2000);
                                                return;
                                            }
                                            setQty((prev) => Math.min(maxQty, prev + 1));
                                        }}
                                    >
                                        <img src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAYCAYAAADgdz34AAAACXBIWXMAAAsTAAALEwEAmpwYAAAANUlEQVR4nGNgGAVUBv+hmGbg/6gFhMD/UQtobsF/KuGBs2DoxwEh8H/UAkLg/6gFo4ABHQAAsEtDvdpF+4wAAAAASUVORK5CYII=" 
                                            alt="plus-math--v1"
                                            className="w-[14px]"
                                        />
                                    </button>
                                    </div>
                                </div>

                                {showMaxMsg && (
                                    <p className="text-sm text-red-500 mt-2">
                                        Maximum available quantity reached
                                    </p>
                                )}

                                <button
                                    className="w-full text-[clamp(10px,0.84vw,12px)] bg-black text-white py-2 rounded transition hover:opacity-90 disabled:opacity-50 mt-4"
                                    disabled={isLoading}
                                    onClick={addToCart}
                                >
                                    {isLoading ? "Adding..." : "Add to cart"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ShopifyAddToCart;
